import classnames from 'classnames';
import { set } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/react-redux';
import { DragDrop } from '../../../../shared/drag-n-drop/DragDrop';
import { DraggableItem } from '../../../../shared/drag-n-drop/shared';
import { Icon } from '../../../../shared/icon/Icon';
import { Tick } from '../../../../shared/icons';
import { WizardTab } from '../../../../shared/wizard/WizardTab';
import { getAllDocumentNames } from '../../../documents/store';
import { DocumentNameFewShotPriorityOrder, updateFewShotPriorityOrder } from '../store';
import styles from './FewShotPriority.module.scss';

interface FewShotPriorityProps {
    priorityOrder: DocumentNameFewShotPriorityOrder;
}

export const FewShotPriority: React.FC<FewShotPriorityProps> = ({ priorityOrder }) => {
    const [selectedDocumentNameId, setSelectedDocumentNameId] = useState<string>(Object.keys(priorityOrder)[0]);
    const dispatch = useAppDispatch();
    const allDocumentNames = useAppSelector(getAllDocumentNames);

    const getDocumentName = useCallback((id: string) => allDocumentNames.find(({ documentNameId }) => documentNameId === parseInt(id))?.documentName || '', [allDocumentNames]);

    const wizardTabs = useMemo(() => Object.keys(priorityOrder).map(documentNameId => ({
        label: getDocumentName(documentNameId),
        selected: documentNameId === selectedDocumentNameId,
        onClick: () => setSelectedDocumentNameId(documentNameId)
    })), [getDocumentName, selectedDocumentNameId, priorityOrder]);

    const savePriority = useCallback((updatedOrder: DocumentNameFewShotPriorityOrder) => dispatch(updateFewShotPriorityOrder(updatedOrder)), [dispatch]);

    const listOptions = useMemo(() => priorityOrder[selectedDocumentNameId]?.map(({ mlFewShotDocumentId, documentDescription, isConfirmed }) => ({ id: mlFewShotDocumentId.toString(), label: documentDescription, isConfirmed })) || [], [priorityOrder, selectedDocumentNameId]);

    const updateList = useCallback((list: DraggableItem[]) => {
        const updatedSelectedDocumentNameOrder = list.map(({ id, label }, index) => ({ mlFewShotDocumentId: parseInt(id), documentDescription: label, priority: index + 1, isConfirmed: listOptions.find(option => option.id === id)!.isConfirmed }));
        const updatedPriorityOrder = set(selectedDocumentNameId, updatedSelectedDocumentNameOrder, priorityOrder);
        savePriority(updatedPriorityOrder);
    }, [selectedDocumentNameId, priorityOrder, savePriority, listOptions]);

    const getChildElement = useCallback((childId: string, index: number) => {
        const listOption = listOptions.find(({ id }) => id === childId);
        if (listOption) {
            return (
                <div className={styles.priorityTileWrapper}>
                    <div className={classnames(styles.priorityTile, { [styles.includedFewShotDocument]: index < 2 })}>
                        <div className={styles.priorityTileOrder}>{index + 1}</div>
                        <div className={styles.priorityTileLabel} data-testid={`few-shot-priority-tile-${childId}`}>{listOption.label}</div>
                        {listOption.isConfirmed === 1 && <div className={styles.confirmedIcon}><Icon icon={Tick} fontSize={20} /></div>}
                    </div>
                    {index === 1 && listOptions.length > 2 && <div className={styles.priorityTileDivider} />}
                </div>
            );
        }
        return null;
    }, [listOptions]);

    const getDraggingElement = useCallback((childId: string, index: number) => {
        const listOption = listOptions.find(({ id }) => id === childId)!;
        return (
            <div className={styles.priorityTileWrapper}>
                <div className={styles.priorityTile}>
                    <div className={styles.priorityTileOrder}>{index + 1}</div>
                    <div className={styles.priorityTileLabel} data-testid={`few-shot-priority-tile-${childId}`}>{listOption.label}</div>
                    {listOption.isConfirmed === 1 && <div className={styles.confirmedIcon}><Icon icon={Tick} fontSize={20} /></div>}
                </div>
            </div>
        );

    }, [listOptions]);

    return (
        <div className={styles.fewShotPriorityWrapper} data-testid='few-shot-priority-wrapper'>
            <div className={styles.wizardTabWrapper}>
                {wizardTabs.map(({ label, onClick, selected }) => (
                    <WizardTab key={label} label={label} onClick={onClick} selected={selected} />
                ))}
            </div>
            <div className={styles.scrollableWrapper}>
                <DragDrop
                    getChildElement={getChildElement}
                    getDraggingElement={getDraggingElement}
                    list={listOptions}
                    listId='few-shot-priority'
                    updateList={updateList}
                />
            </div>
        </div>
    );
};
